/**
 * Unified Scripts table field helpers (legacy + new API names).
 * DB columns: ScriptsFeederName, Description, BaseSymbol, ContractSize, TickDigits, ExpiryDateTime, ScriptName
 */

function pickField(row, ...keys) {
  if (row == null) return undefined;
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null) return row[k];
  }
  return undefined;
}

function reqStr(v, fallback = "") {
  return v == null || v === "" ? fallback : String(v);
}

function optStr(v) {
  return v == null || v === "" ? null : String(v);
}

function num(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

/** Trading / feed identity — ScriptsFeederName (formerly DisplayName). */
export function scriptsFeederNameOf(row) {
  return String(
    pickField(
      row,
      "scriptsFeederName",
      "ScriptsFeederName",
      "displayName",
      "DisplayName"
    ) ?? ""
  ).trim();
}

/** Human-readable Script Manager label — ScriptName. */
export function scriptNameOf(row) {
  return String(
    pickField(row, "scriptName", "ScriptName", "script", "Script") ?? ""
  ).trim();
}

/** @deprecated use scriptsFeederNameOf */
export function displayNameOf(row) {
  return scriptsFeederNameOf(row);
}

export const SCRIPT_UI_COLUMNS = [
  "SegmentId",
  "ScriptsFeederName",
  "Description",
  "BaseSymbol",
  "ContractSize",
  "MinLot",
  "TickDigits",
  "Multiplier",
  "SeriesGroup",
  "InstrType",
  "OptType",
  "StrikePrice",
  "ExpiryDateTime",
  "ScriptName",
];

export const SCRIPT_UI_COLUMN_LABELS = {
  SegmentId: "Segment",
  ScriptsFeederName: "Scripts Feeder Name",
  Description: "Description",
  BaseSymbol: "Base Symbol",
  ContractSize: "Contract Size",
  MinLot: "Min Lot",
  TickDigits: "Tick Digits",
  Multiplier: "Multiplier",
  SeriesGroup: "Series Group",
  InstrType: "Instr Type",
  OptType: "Opt Type",
  StrikePrice: "Strike Price",
  ExpiryDateTime: "Expiry Date and Time",
  ScriptName: "Script Name",
};

export const SCRIPT_DB_TABLE_COLUMNS = [
  { label: "Segment", key: "segmentId" },
  { label: "Scripts Feeder Name", key: "scriptsFeederName" },
  { label: "Base Symbol", key: "baseSymbol" },
  { label: "Expiry Date and Time", key: "expiryDateTime" },
  { label: "Script Name", key: "scriptName" },
];

export const SCRIPT_CSV_UPLOAD_COLUMNS = [
  { label: "Segment", key: "SegmentName" },
  { label: "Scripts Feeder Name", key: "ScriptsFeederName" },
  { label: "Base Symbol", key: "BaseSymbol" },
  { label: "Expiry Date and Time", key: "ExpiryDateTime" },
  { label: "Script Name", key: "ScriptName" },
];

export const SCRIPT_DEFAULT_OPTIONAL_VALUES = {
  ContractSize: 10,
  MinLot: 1,
  TickDigits: 100,
  Multiplier: 100.0,
  StrikePrice: 0,
  Description: "MCX",
  OptType: "XX",
  InstrType: "FC",
  SeriesGroup: "FC",
};

function formatExpiryForUi(v) {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (s === "0") return "";
  return s;
}

/** API / DB list row → PascalCase form used by Update Script modals. */
export function normalizeScriptRowForUi(row) {
  if (!row) return {};
  return {
    id: pickField(row, "id", "Id"),
    SegmentId: pickField(row, "segmentId", "SegmentId"),
    ScriptsFeederName: scriptsFeederNameOf(row),
    Description: reqStr(pickField(row, "description", "Description", "exchange", "Exchange")),
    BaseSymbol: reqStr(pickField(row, "baseSymbol", "BaseSymbol", "symbol", "Symbol")),
    ContractSize: num(pickField(row, "contractSize", "ContractSize", "lotSize", "LotSize"), 0),
    MinLot: num(pickField(row, "minLot", "MinLot"), 0),
    TickDigits: num(pickField(row, "tickDigits", "TickDigits", "tickSize", "TickSize"), 0),
    Multiplier: num(pickField(row, "multiplier", "Multiplier"), 0),
    SeriesGroup: reqStr(pickField(row, "seriesGroup", "SeriesGroup")),
    InstrType: reqStr(pickField(row, "instrType", "InstrType")),
    OptType: optStr(pickField(row, "optType", "OptType")),
    StrikePrice: reqStr(pickField(row, "strikePrice", "StrikePrice"), "0"),
    ExpiryDateTime: formatExpiryForUi(
      pickField(row, "expiryDateTime", "ExpiryDateTime", "expiryDate", "ExpiryDate")
    ),
    ScriptName: reqStr(pickField(row, "scriptName", "ScriptName", "script", "Script")),
  };
}

/** CSV upload row → PascalCase preview row (accepts old + new headers). */
export function normalizeScriptRowFromCsv(row) {
  return {
    SegmentName: reqStr(pickField(row, "SegmentName", "segmentName")),
    ScriptsFeederName: reqStr(
      pickField(row, "ScriptsFeederName", "scriptsFeederName", "DisplayName", "displayName")
    ),
    Description: optStr(pickField(row, "Description", "description", "Exchange", "exchange")),
    Token: optStr(pickField(row, "Token", "token")),
    BaseSymbol: reqStr(pickField(row, "BaseSymbol", "baseSymbol", "Symbol", "symbol")),
    ContractSize: num(pickField(row, "ContractSize", "contractSize", "LotSize", "lotSize"), 0),
    MinLot: num(pickField(row, "minLot", "MinLot"), 0),
    TickDigits: num(pickField(row, "TickDigits", "tickDigits", "TickSize", "tickSize"), 0),
    Multiplier: num(pickField(row, "Multiplier", "multiplier"), 0),
    SeriesGroup: reqStr(pickField(row, "SeriesGroup", "seriesGroup")),
    InstrType: reqStr(pickField(row, "InstrType", "instrType")),
    OptType: optStr(pickField(row, "OptType", "optType")),
    StrikePrice: reqStr(pickField(row, "StrikePrice", "strikePrice"), "0"),
    ExpiryDateTime: reqStr(
      pickField(row, "ExpiryDateTime", "expiryDateTime", "ExpiryDate", "expiryDate")
    ),
    ScriptName: reqStr(pickField(row, "ScriptName", "scriptName", "Script", "script")),
    Isin: optStr(pickField(row, "Isin", "isin")),
    AssetToken: optStr(pickField(row, "AssetToken", "assetToken")),
    Unit: optStr(pickField(row, "Unit", "unit")),
  };
}

/** Form / CSV row → API body (camelCase unified names). */
export function toScriptsUpdateApiBody(row) {
  const ui = normalizeScriptRowForUi(row);
  return {
    segmentName: reqStr(pickField(row, "SegmentName", "segmentName")),
    scriptsFeederName: reqStr(
      pickField(row, "ScriptsFeederName", "scriptsFeederName") || ui.ScriptsFeederName
    ),
    description: optStr(pickField(row, "Description", "description") ?? ui.Description),
    token: optStr(pickField(row, "Token", "token")),
    baseSymbol: reqStr(pickField(row, "BaseSymbol", "baseSymbol") || ui.BaseSymbol),
    contractSize: num(pickField(row, "ContractSize", "contractSize") ?? ui.ContractSize, 0),
    minLot: num(pickField(row, "MinLot", "minLot") ?? ui.MinLot, 0),
    tickDigits: num(pickField(row, "TickDigits", "tickDigits") ?? ui.TickDigits, 0),
    multiplier: num(pickField(row, "Multiplier", "multiplier") ?? ui.Multiplier, 0),
    seriesGroup: reqStr(pickField(row, "SeriesGroup", "seriesGroup") ?? ui.SeriesGroup),
    instrType: reqStr(pickField(row, "InstrType", "instrType") ?? ui.InstrType),
    optType: optStr(pickField(row, "OptType", "optType") ?? ui.OptType),
    strikePrice: reqStr(pickField(row, "StrikePrice", "strikePrice") ?? ui.StrikePrice, "0"),
    expiryDateTime: reqStr(
      pickField(row, "ExpiryDateTime", "expiryDateTime") ?? ui.ExpiryDateTime
    ),
    isin: optStr(pickField(row, "Isin", "isin")),
    assetToken: optStr(pickField(row, "AssetToken", "assetToken")),
    unit: optStr(pickField(row, "Unit", "unit")),
    scriptName: reqStr(pickField(row, "ScriptName", "scriptName") || ui.ScriptName),
  };
}

/** Full edit payload for settings/script-action EDIT. */
export function toEditScriptApiBody(row) {
  return {
    id: Number(pickField(row, "id", "Id")),
    segmentId: Number(pickField(row, "segmentId", "SegmentId", "SegmentId") ?? 0),
    ...toScriptsUpdateApiBody(row),
  };
}

/**
 * Normalize trading / market-watch / mobile script rows.
 * Ensures legacy keys (displayName, symbol, script, expiryDate, lotSize) exist
 * alongside unified Scripts table names.
 */
export function normalizeTradingScriptRow(row) {
  if (row == null || typeof row !== "object") return row;

  const feeder = scriptsFeederNameOf(row);
  const baseSymbol = String(
    pickField(row, "baseSymbol", "BaseSymbol", "symbol", "Symbol") ?? ""
  ).trim();
  const scriptName = String(
    pickField(row, "scriptName", "ScriptName", "script", "Script") ?? ""
  ).trim();
  const expiry = pickField(
    row,
    "expiryDateTime",
    "ExpiryDateTime",
    "expiryDate",
    "ExpiryDate"
  );
  const contractSize = num(
    pickField(row, "contractSize", "ContractSize", "lotSize", "LotSize", "qty", "Qty"),
    0
  );
  const tickDigits = num(
    pickField(row, "tickDigits", "TickDigits", "tickSize", "TickSize"),
    0
  );
  const strike = pickField(row, "strike", "Strike", "strikePrice", "StrikePrice");

  const displayString =
    pickField(row, "displayString", "DisplayString") ??
    row.displayString ??
    row.DisplayString;

  return {
    ...row,
    displayString: displayString ?? (feeder || scriptName || baseSymbol || ""),
    DisplayString: displayString ?? (feeder || scriptName || baseSymbol || ""),
    displayName: feeder || row.displayName || row.DisplayName || "",
    DisplayName: feeder || row.DisplayName || row.displayName || "",
    scriptsFeederName: feeder || row.scriptsFeederName,
    symbol: baseSymbol || row.symbol || row.Symbol || "",
    Symbol: baseSymbol || row.Symbol || row.symbol || "",
    baseSymbol: baseSymbol || row.baseSymbol,
    script: scriptName || row.script || row.Script || "",
    Script: scriptName || row.Script || row.script || "",
    scriptName: scriptName || row.scriptName,
    expiryDate: expiry ?? row.expiryDate ?? row.ExpiryDate,
    ExpiryDate: expiry ?? row.ExpiryDate ?? row.expiryDate,
    expiryDateTime: expiry ?? row.expiryDateTime,
    lotSize: contractSize,
    LotSize: contractSize,
    contractSize,
    tickSize: tickDigits || row.tickSize,
    TickSize: tickDigits || row.TickSize,
    tickDigits,
    strike: strike ?? row.strike,
    strikePrice: strike ?? row.strikePrice ?? row.StrikePrice,
    optType: pickField(row, "optType", "OptType") ?? row.optType,
  };
}

export function normalizeTradingScriptList(list) {
  if (!Array.isArray(list)) return list;
  return list.map((row) => normalizeTradingScriptRow(row));
}

/** Tree / list display label for Script Manager (Script Name first). */
export function scriptTreeLabelOf(row) {
  const name = scriptNameOf(row);
  if (name) return name;
  const feeder = scriptsFeederNameOf(row);
  if (feeder) return feeder;
  const base = String(pickField(row, "baseSymbol", "BaseSymbol", "symbol", "Symbol") ?? "").trim();
  if (base) return base;
  const id = pickField(row, "id", "Id");
  return id != null ? `Script ${id}` : "Script";
}
